import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import type { CrmDealDetail, CrmDealListItem, CrmStage } from '@ip/shared-types';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import { dealHealth, indexStages, isClosedDeal } from './dealMath';

export interface MoveOptions {
  /** The move reverts the previous one (undo toast) — it gets no undo of its own. */
  undo?: boolean;
  /** Set by CloseDealDialog after the user confirmed a won/lost move. */
  confirmed?: boolean;
  lostReason?: string;
  note?: string;
}

export interface CloseRequest {
  mode: 'won' | 'lost';
  dealId: string;
  targetStageId: string;
}

export type MoveDealFn = (dealId: string, targetStageId: string, opts?: MoveOptions) => Promise<void>;

const SHOW_CLOSED_KEY = 'crm.board.showClosed';
/** How far back the won/lost rails reach; the rail header names it. */
export const CLOSED_WINDOW_DAYS = 30;
const UNDO_TOAST_MS = 6000;

function readShowClosed(): boolean {
  try {
    return localStorage.getItem(SHOW_CLOSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Board state and the ONE stage-change path (`moveDeal`). Drag, stepper,
 * shortcuts, next-step card and undo all end up here, so optimistic patching,
 * the won/lost confirmation gate, rollback and the undo toast exist once.
 */
export function useDealBoard(nip: string, currentUserId: string | null) {
  const { listStages, listDeals, changeDealStage } = useCrmApi();

  const [stages, setStages] = useState<CrmStage[]>([]);
  const [deals, setDealsState] = useState<CrmDealListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPendingState] = useState<ReadonlySet<string>>(() => new Set());
  const [closeRequest, setCloseRequest] = useState<CloseRequest | null>(null);

  const [search, setSearch] = useState('');
  const [mine, setMine] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showClosed, setShowClosedState] = useState<boolean>(readShowClosed);

  // Every derived figure is calendar-day based, so `now` only changes when
  // the day rolls over — it stays referentially stable in between and the
  // memoised cards do not re-render every minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(
      () => setNow((prev) => (dayjs(prev).isSame(new Date(), 'day') ? prev : new Date())),
      60_000,
    );
    return () => window.clearInterval(id);
  }, []);

  // Refs mirror the state so `moveDeal` (called from toast actions and dnd
  // callbacks created long ago) always reads the current board, never a
  // stale closure.
  const dealsRef = useRef(deals);
  const pendingRef = useRef(pending);
  const stagesRef = useRef(stages);
  stagesRef.current = stages;

  const setDeals = useCallback((updater: (prev: CrmDealListItem[]) => CrmDealListItem[]) => {
    dealsRef.current = updater(dealsRef.current);
    setDealsState(dealsRef.current);
  }, []);

  const setPending = useCallback((updater: (prev: Set<string>) => Set<string>) => {
    pendingRef.current = updater(new Set(pendingRef.current));
    setPendingState(pendingRef.current);
  }, []);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      // Two requests on purpose: closed history is capped separately, so it can
      // never push open deals past the server's 500-row limit.
      const closedAfter = dayjs().subtract(CLOSED_WINDOW_DAYS, 'day').toISOString();
      const [stageList, openDeals, closedDeals] = await Promise.all([
        listStages(nip),
        listDeals(nip, { open: true }),
        listDeals(nip, { closedAfter }),
      ]);
      if (seq !== loadSeq.current) return;
      const seen = new Set(openDeals.map((deal) => deal.id));
      const merged = [...openDeals, ...closedDeals.filter((deal) => !seen.has(deal.id))];
      setStages(stageList);
      setDeals(() => merged);
      setForbidden(false);
      setLoadError(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) setForbidden(true);
      else {
        setLoadError(true);
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, listStages, listDeals, setDeals]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // The closed window is always loaded; the toggle only widens the rails.
  const setShowClosed = useCallback((value: boolean) => {
    setShowClosedState(value);
    try {
      localStorage.setItem(SHOW_CLOSED_KEY, value ? '1' : '0');
    } catch {
      // Private mode / blocked storage — the toggle simply does not persist.
    }
  }, []);

  const stageIndex = useMemo(() => indexStages(stages), [stages]);

  /** Upsert by id — the list keeps the detail's extra keys, which is harmless. */
  const applyDeal = useCallback(
    (detail: CrmDealDetail) => {
      setDeals((prev) => {
        const index = prev.findIndex((deal) => deal.id === detail.id);
        if (index < 0) return [detail, ...prev];
        const next = prev.slice();
        next[index] = { ...prev[index], ...detail };
        return next;
      });
    },
    [setDeals],
  );

  /** A logged activity clears the card's "brak kontaktu" without a reload. */
  const touchActivity = useCallback(
    (dealId: string, occurredAt: string) => {
      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === dealId && (!deal.lastActivityAt || deal.lastActivityAt < occurredAt)
            ? { ...deal, lastActivityAt: occurredAt }
            : deal,
        ),
      );
    },
    [setDeals],
  );

  const moveDealRef = useRef<MoveDealFn>(async () => undefined);

  const undoAction = useCallback(
    (dealId: string, previousStageId: string) => ({
      label: t('common.undo'),
      onClick: () =>
        void moveDealRef.current(dealId, previousStageId, { undo: true, note: t('crm.deals.board.undoNote') }),
    }),
    [],
  );

  const moveDeal = useCallback<MoveDealFn>(
    async (dealId, targetStageId, opts = {}) => {
      const target = stagesRef.current.find((stage) => stage.id === targetStageId);
      const prev = dealsRef.current.find((deal) => deal.id === dealId);
      // 1. Nothing to do — unknown target, same stage, or a move already in flight.
      if (!target || !prev || prev.stageId === targetStageId || pendingRef.current.has(dealId)) return;

      // 2. Won/lost need a confirmation (lost: the reason is mandatory; won:
      //    the account irreversibly becomes a customer). The card stays put.
      if ((target.isWon || target.isLost) && !opts.confirmed) {
        setCloseRequest({ mode: target.isWon ? 'won' : 'lost', dealId, targetStageId });
        return;
      }

      // 3. Confirmed terminal move — not optimistic, the dialog shows the spinner.
      if (target.isWon || target.isLost) {
        setPending((set) => set.add(dealId));
        try {
          const detail = await changeDealStage(nip, dealId, {
            stageId: targetStageId,
            ...(opts.lostReason ? { lostReason: opts.lostReason } : {}),
            ...(opts.note ? { note: opts.note } : {}),
          });
          applyDeal(detail);
          setCloseRequest(null);
          if (target.isWon) {
            toast.success(t('crm.deals.board.won', { name: prev.name }), {
              description: t('crm.deals.close.wonAccountStays'),
              duration: UNDO_TOAST_MS,
              action: undoAction(dealId, prev.stageId),
            });
          } else {
            toast.success(t('crm.deals.board.lost'), {
              duration: UNDO_TOAST_MS,
              action: undoAction(dealId, prev.stageId),
            });
          }
        } finally {
          setPending((set) => {
            set.delete(dealId);
            return set;
          });
        }
        return;
      }

      // 4. Open target — optimistic, rolled back on failure. The deal moves to
      //    the FRONT of the list, so it lands at the top of its new column
      //    (where the user's eye — and the drop — just went) instead of at
      //    whatever position its original load order happens to give it.
      const nowIso = new Date().toISOString();
      setDeals((list) => {
        const current = list.find((deal) => deal.id === dealId);
        if (!current) return list;
        const moved: CrmDealListItem = {
          ...current,
          stageId: target.id,
          stageKey: target.key,
          stageName: target.name,
          probability: target.probability,
          closedAt: null,
          stageEnteredAt: nowIso,
          updatedAt: nowIso,
        };
        return [moved, ...list.filter((deal) => deal.id !== dealId)];
      });
      setPending((set) => set.add(dealId));
      try {
        const detail = await changeDealStage(nip, dealId, {
          stageId: targetStageId,
          ...(opts.note ? { note: opts.note } : {}),
        });
        applyDeal(detail);
        // A move out of won/lost is a resume, whoever triggered it.
        const wasClosed = isClosedDeal(prev, stagesRef.current.find((stage) => stage.id === prev.stageId));
        const message = wasClosed
          ? t('crm.deals.board.resumed')
          : t('crm.deals.board.movedTo', { stage: target.name });
        toast.success(message, {
          duration: UNDO_TOAST_MS,
          // An undo of an undo is pointless noise; every other move is reversible.
          ...(opts.undo ? {} : { action: undoAction(dealId, prev.stageId) }),
        });
      } catch (error) {
        // Roll back ONLY the stage fields. Restoring the whole `prev` snapshot
        // would silently discard a field save (name, value, owner…) that the
        // sheet completed while this request was in flight.
        setDeals((list) =>
          list.map((deal) =>
            deal.id === dealId
              ? {
                  ...deal,
                  stageId: prev.stageId,
                  stageKey: prev.stageKey,
                  stageName: prev.stageName,
                  probability: prev.probability,
                  closedAt: prev.closedAt,
                  stageEnteredAt: prev.stageEnteredAt,
                  updatedAt: prev.updatedAt,
                }
              : deal,
          ),
        );
        toast.error(
          (error as { status?: number })?.status === 403
            ? t('errors.accessDenied')
            : t('crm.deals.board.moveFailed'),
        );
      } finally {
        setPending((set) => {
          set.delete(dealId);
          return set;
        });
      }
    },
    [nip, changeDealStage, applyDeal, setDeals, setPending, undoAction],
  );
  moveDealRef.current = moveDeal;

  /** Called by CloseDealDialog; throws so the dialog can show the inline error. */
  const confirmClose = useCallback(
    async (input: { lostReason?: string; note?: string }) => {
      if (!closeRequest) return;
      await moveDeal(closeRequest.dealId, closeRequest.targetStageId, { confirmed: true, ...input });
    },
    [closeRequest, moveDeal],
  );

  const cancelClose = useCallback(() => setCloseRequest(null), []);

  const visibleDeals = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return deals.filter((deal) => {
      if (mine && deal.ownerUserId !== currentUserId) return false;
      if (overdueOnly && dealHealth(deal, now, stageIndex.byId.get(deal.stageId)) !== 'critical') return false;
      if (
        needle &&
        !deal.name.toLocaleLowerCase().includes(needle) &&
        !deal.accountName.toLocaleLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [deals, search, mine, overdueOnly, currentUserId, now, stageIndex]);

  return {
    stages,
    stageIndex,
    deals,
    visibleDeals,
    loading,
    forbidden,
    loadError,
    pending,
    now,
    load,
    moveDeal,
    applyDeal,
    touchActivity,
    closeRequest,
    confirmClose,
    cancelClose,
    filters: { search, setSearch, mine, setMine, overdueOnly, setOverdueOnly, showClosed, setShowClosed },
  };
}
